import { uniqBy } from 'lodash/fp';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import { User } from 'dashboard/models/user';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

interface FormCaseTemplateSettingsGeneralProps {
  users: User[];
}

const FormCaseTemplateSettingsGeneral = ({ users }: FormCaseTemplateSettingsGeneralProps) => {
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext<{
    'case-template-name': string;
    'case-template-prefix': string;
    'case-creation-email-addresses': string[];
  }>();

  const subscribersFormState = watch('case-creation-email-addresses');

  const subscriberOptions = useMemo(() => {
    const userOptions = users.map((user) => {
      return {
        label: user.attributes['email-address'] ?? '',
        value: user.attributes['email-address'] ?? '',
      };
    });

    const createdOptions = subscribersFormState.map((emailAddress: string) => {
      return {
        label: emailAddress,
        value: emailAddress,
      };
    });

    return uniqBy('value', [...userOptions, ...createdOptions]);
  }, [users, subscribersFormState]);

  return (
    <>
      <Card>
        <Card.Body>
          <Heading size='sm'>Name</Heading>
          <Paragraph>Customize the name of your cases.</Paragraph>
          <Spacer height={2} />
          <Container>
            <div>
              <Label htmlFor='case-template-name' size='lg' weight='bold'>
                Case template name
              </Label>
              <Spacer height={1} />
              <Input
                {...register('case-template-name', {
                  required: 'Please enter a case template name',
                })}
                name='case-template-name'
                id='case-template-name'
                type='text'
              />
              {errors['case-template-name'] && (
                <FormError>{errors['case-template-name'].message}</FormError>
              )}
            </div>
          </Container>

          <Spacer height={2} />

          <Container>
            <div>
              <Label htmlFor='case-template-prefix' size='lg' weight='bold'>
                Case template key
              </Label>
              <Spacer height={1} />
              <Input
                {...register('case-template-prefix', {
                  required: 'Please enter a case template key',
                  minLength: {
                    value: 2,
                    message: 'Case template key must be at least 2 letters long',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: 'Case keys must only contain letters and numbers',
                  },
                })}
                name='case-template-prefix'
                id='case-template-prefix'
                type='text'
              />
              {errors['case-template-prefix'] && (
                <FormError>{errors['case-template-prefix'].message}</FormError>
              )}
            </div>
          </Container>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Heading size='sm'>Case notifications</Heading>
          <Paragraph>Receive email notifications when a case is created.</Paragraph>
          <Spacer height={2} />
          <Container>
            <Controller
              control={control}
              name='case-creation-email-addresses'
              render={({ field: { onChange, value } }) => (
                <ReactCreatableSelect
                  id='case-creation-email-addresses'
                  value={value}
                  options={subscriberOptions || []}
                  onChange={onChange}
                  isMulti={true}
                  isSearchable={true}
                  portal={true}
                  onCreateOption={(value: string) => onChange(subscribersFormState.concat([value]))}
                  placeholder='Emails...'
                />
              )}
            />
          </Container>
        </Card.Body>
      </Card>
    </>
  );
};

export default FormCaseTemplateSettingsGeneral;
